/**
 * Anchor Sub Menu behavior:
 * - smooth scroll to sections
 * - keep URL hash in sync
 * - keep selected tab visible on horizontal mobile list
 */
const isMedium = require('../utils/isMedium');
const anchorDomUtils = require('../utils/anchorDomUtils');

(function initLvzAnchorSubMenu(windowRef, $) {
	const ROOT_SELECTOR = '[data-lvz-anchor-sub-menu]';
	const LINK_SELECTOR = '[data-lvz-anchor-link]';
	const PD_STICKY_HOST_SELECTOR = '.experience-commerce_assets-anchor_sub_menu-anchorContainer';
	const ACTIVE_LINK_CLASS = 'is-active';
	let resizeTimer;
	let scrollRafPending = false;
	let refreshRafPending = false;
	let domObserver = null;
	let headerObserver = null;

	// Resolves the destination id from explicit data attribute or link href.
	function findTargetId($link) {
		const explicitId = anchorDomUtils.sanitizeAnchorId($link.attr('data-lvz-anchor-id') || '');
		if (explicitId) {
			return explicitId;
		}
		return anchorDomUtils.sanitizeAnchorId($link.attr('href') || '');
	}

	// Navigation-style active state: we track the selected anchor with a class instead of
	// tab semantics (`aria-selected` / `role="tab"`), because this component is a section nav.
	function syncSelectedState($roots, selectedId) {
		const targetId = anchorDomUtils.sanitizeAnchorId(selectedId);

		$roots.each(function () {
			$(this)
				.find(LINK_SELECTOR)
				.each(function () {
					const $link = $(this);
					const isSelected = !!targetId && findTargetId($link) === targetId;
					$link.toggleClass(ACTIVE_LINK_CLASS, isSelected);
				});
		});
	}

	// The server exposes edit-mode state on the root so the client can disable sticky behavior
	// specifically inside Page Designer, where the wrapper is an editor artifact.
	function isEditModeRoot($root) {
		return String($root.attr('data-lvz-anchor-sub-menu-edit') || '').toLowerCase() === 'true';
	}

	// Always query live DOM roots because Page Designer can replace subtree markup after load.
	function getRoots() {
		return $(ROOT_SELECTOR);
	}

	// Accessibility: after smooth scroll, move focus to the target section. If the target
	// is not normally focusable, assign a temporary tabindex and clean it up on blur.
	function moveFocusToTarget($target) {
		const node = $target && $target[0];

		if (!node || typeof node.focus !== 'function') {
			return;
		}

		const existingTabindex = node.getAttribute('tabindex');
		if (existingTabindex === null) {
			node.setAttribute('tabindex', '-1');
			$(node).one('blur.lvzAnchorSubMenu', function onTemporaryFocusBlur() {
				node.removeAttribute('tabindex');
			});
		}

		node.focus({ preventScroll: true });
	}

	// Return the rendered bottom edge of a sticky layer only when it is actually visible.
	// This avoids counting hidden/collapsed header rows in the sticky offset calculation.
	function getVisibleBottom(selector) {
		const el = document.querySelector(selector);
		if (!el) {
			return 0;
		}

		const rect = el.getBoundingClientRect();
		if (rect.height < 1 || rect.bottom <= 0) {
			return 0;
		}

		return rect.bottom;
	}

	// Read the real sticky header stack from the viewport instead of summing static heights.
	// This keeps the submenu aligned even when banner/header info rows appear or collapse on scroll.
	function getHeaderAndBannerOffset() {
		const selectors = ['#lvz-top-banner', '#lvz-header-template'];
		if (isMedium()) {
			selectors.push('#lvz-header-info-container');
		}

		return selectors.reduce(function (maxBottom, selector) {
			return Math.max(maxBottom, getVisibleBottom(selector));
		}, 0);
	}

	// Page Designer wraps markup in `.experience-…-anchorContainer`; `top` must be applied
	// there because that node holds `position: sticky` (see _anchorSubMenu.scss).
	function getStickyOffsetTarget($root) {
		const $host = $root.closest(PD_STICKY_HOST_SELECTOR);
		return $host.length ? $host : $root;
	}

	// Applies storefront sticky behavior, but disables it inside Page Designer where the
	// editor wrapper is not part of the real page layout and would visually escape its slot.
	function applyStickyTopOffset($roots) {
		const topOffset = getHeaderAndBannerOffset();
		$roots.each(function applyTopToHost() {
			const $root = $(this);
			const $target = getStickyOffsetTarget($root);

			if (isEditModeRoot($root)) {
				$target.css({
					position: 'static',
					top: 'auto',
				});
				return;
			}

			$target.css({
				position: 'sticky',
				top: topOffset,
			});
		});
	}

	// Smoothly scrolls the page to the target section while accounting for sticky UI.
	function scrollToTarget($root, targetId, options) {
		const targetNode = anchorDomUtils.getAnchorTarget(targetId, document);
		const $target = targetNode ? $(targetNode) : $();
		const settings = options || {};
		if (!$target.length) {
			return false;
		}

		const anchorMenuH = $root.outerHeight() || 0;
		let destination = $target.offset().top - getHeaderAndBannerOffset() - anchorMenuH;
		if (destination < 0) {
			destination = 0;
		}
		$('html, body')
			.stop(true)
			.animate(
				{
					scrollTop: destination,
				},
				350,
				function onScrollComplete() {
					if (settings.focusTarget) {
						moveFocusToTarget($target);
					}
				}
			);
		return true;
	}

	// Scrolls to the section currently present in the URL hash, if any.
	function scrollFromCurrentHash($root) {
		const hashId = anchorDomUtils.sanitizeAnchorId(windowRef.location.hash);
		if (!hashId) {
			return;
		}
		scrollToTarget($root, hashId);
	}

	// Update the hash without polluting browser history on each click; also re-sync the visual
	// active state immediately because `replaceState` does not emit a native hashchange event.
	function updateHash(targetId) {
		const newHash = '#' + targetId;
		if (windowRef.location.hash === newHash) {
			return;
		}

		if (windowRef.history && windowRef.history.replaceState) {
			windowRef.history.replaceState(null, '', newHash);
			syncSelectedState(getRoots(), targetId);
			return;
		}

		windowRef.location.hash = newHash;
	}

	// Keeps the selected tab centered in horizontally scrollable submenu lists.
	function keepSelectedTabInView($root) {
		const $selected = $root.find(LINK_SELECTOR + '.' + ACTIVE_LINK_CLASS).first();
		if (!$selected.length) {
			return;
		}

		if ($selected[0] && typeof $selected[0].scrollIntoView === 'function') {
			$selected[0].scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}
	}

	// Handles submenu link clicks by validating target, scrolling, and syncing hash.
	function onAnchorClick(e) {
		const $link = $(e.currentTarget);
		const $root = $link.closest(ROOT_SELECTOR);
		const targetId = findTargetId($link);

		if (!$root.length || !targetId) {
			return;
		}

		const hasTarget = !!anchorDomUtils.getAnchorTarget(targetId, document);
		if (!hasTarget) {
			return;
		}

		e.preventDefault();
		scrollToTarget($root, targetId, { focusTarget: true });
		updateHash(targetId);
		keepSelectedTabInView($root);
	}

	// Binds delegated click events for submenu anchor links on each root.
	function bindAnchorClicks($roots) {
		$roots.each(function () {
			const $root = $(this);
			$root.off('click.lvzAnchorSubMenu', LINK_SELECTOR);
			$root.on('click.lvzAnchorSubMenu', LINK_SELECTOR, onAnchorClick);
		});
	}

	// Re-applies tab visibility and anchor scrolling whenever the hash changes.
	function bindHashSync() {
		$(windowRef).off('hashchange.lvzAnchorSubMenu');
		$(windowRef).on('hashchange.lvzAnchorSubMenu', function onHashChange() {
			syncSelectedState(getRoots(), windowRef.location.hash);
			getRoots().each(function () {
				const $root = $(this);
				keepSelectedTabInView($root);
				scrollFromCurrentHash($root);
			});
		});
	}

	// Recomputes sticky offsets on resize, header search layout changes, and scroll events.
	function bindOffsetRecalculation() {
		$(windowRef).off('resize.lvzAnchorSubMenu');
		$(windowRef).on('resize.lvzAnchorSubMenu', function onResize() {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(function applyOffsetAfterResize() {
				applyStickyTopOffset(getRoots());
			}, 150);
		});

		// The mobile search bar expands/collapses inside the sticky header: follow its new bottom edge.
		$(document).off('lavazza:headerSearchLayoutChange.lvzAnchorSubMenu');
		$(document).on('lavazza:headerSearchLayoutChange.lvzAnchorSubMenu', function onHeaderSearchLayoutChange() {
			windowRef.requestAnimationFrame(function applyOffsetAfterSearchLayoutChange() {
				applyStickyTopOffset(getRoots());
			});
		});

		$(windowRef).off('scroll.lvzAnchorSubMenu');
		$(windowRef).on('scroll.lvzAnchorSubMenu', function onScrollRecalc() {
			if (scrollRafPending) {
				return;
			}

			scrollRafPending = true;
			windowRef.requestAnimationFrame(function applyOffsetAfterScroll() {
				applyStickyTopOffset(getRoots());
				scrollRafPending = false;
			});
		});
	}

	// Centralized re-sync used both on first load and when Page Designer mutates the canvas.
	// Rebinds click handlers, recomputes sticky offset, and restores active-link state.
	function refreshSubMenus() {
		const $roots = getRoots();
		if (!$roots.length) {
			return;
		}

		applyStickyTopOffset($roots);
		bindAnchorClicks($roots);
		syncSelectedState($roots, windowRef.location.hash);

		$roots.each(function () {
			keepSelectedTabInView($(this));
		});
	}

	// Coalesce multiple DOM/header mutations into a single refresh on the next animation frame.
	function scheduleRefresh() {
		if (refreshRafPending) {
			return;
		}

		refreshRafPending = true;
		windowRef.requestAnimationFrame(function refreshOnNextFrame() {
			refreshSubMenus();
			refreshRafPending = false;
		});
	}

	// Page Designer can add/remove anchor items live without a full page reload.
	// Watch for those subtree changes so the component re-initializes immediately in the canvas.
	function mutationTouchesAnchorSubMenu(node) {
		if (!node || node.nodeType !== 1) {
			return false;
		}

		if (
			node.matches &&
			(node.matches(ROOT_SELECTOR) || node.matches(PD_STICKY_HOST_SELECTOR) || node.matches(LINK_SELECTOR))
		) {
			return true;
		}

		return !!(
			node.querySelector && node.querySelector(ROOT_SELECTOR + ', ' + PD_STICKY_HOST_SELECTOR + ', ' + LINK_SELECTOR)
		);
	}

	// Observe generic DOM insertions/removals so newly added submenu items in Page Designer
	// inherit all JS behavior before the editor performs a full refresh.
	function bindDomRefreshObserver() {
		if (domObserver || !windowRef.MutationObserver || !document.body) {
			return;
		}

		domObserver = new MutationObserver(function onDomMutations(mutations) {
			const shouldRefresh = mutations.some(function (mutation) {
				const nodes = Array.prototype.slice
					.call(mutation.addedNodes || [])
					.concat(Array.prototype.slice.call(mutation.removedNodes || []));
				return nodes.some(mutationTouchesAnchorSubMenu);
			});

			if (shouldRefresh) {
				scheduleRefresh();
			}
		});

		domObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	// Header sticky layers change class/style while the user scrolls.
	// Observe those nodes so the submenu top offset is refreshed when header composition changes.
	function bindHeaderStateObserver() {
		if (headerObserver || !windowRef.MutationObserver) {
			return;
		}

		const targets = ['lvz-top-banner', 'lvz-header-info-container', 'lvz-header-template', 'sg-navbar-collapse']
			.map(function (id) {
				return document.getElementById(id);
			})
			.filter(Boolean);

		if (!targets.length) {
			return;
		}

		headerObserver = new MutationObserver(function onHeaderMutations() {
			scheduleRefresh();
		});

		targets.forEach(function (target) {
			headerObserver.observe(target, {
				attributes: true,
				attributeFilter: ['class', 'style', 'aria-hidden'],
				subtree: false,
			});
		});
	}

	// Runs initial hash-based sync, delaying execution to avoid first-paint jumps.
	function runInitialHashSync($roots) {
		const executeInitialSync = function () {
			$roots.each(function () {
				const $root = $(this);
				keepSelectedTabInView($root);
				scrollFromCurrentHash($root);
			});
		};
		const hasInitialHash = !!anchorDomUtils.sanitizeAnchorId(windowRef.location.hash);

		if (hasInitialHash) {
			setTimeout(executeInitialSync, 500);
			return;
		}

		executeInitialSync();
	}

	// Initializes anchor submenu behavior when the DOM is ready.
	$(function onReady() {
		const $roots = getRoots();
		if (!$roots.length) {
			return;
		}

		refreshSubMenus();
		bindHashSync();
		bindOffsetRecalculation();
		bindDomRefreshObserver();
		bindHeaderStateObserver();

		// If the page opens with a hash, delay initial sync to avoid sticky jump during first paint.
		runInitialHashSync($roots);
	});
})(window, jQuery);
